import { defineStore } from 'pinia';
import {
  COOKIE_CONSENT_CHANGE_KEY,
  ICookieConsent,
  ICookieConsentSelection,
  createCookieConsent,
  readCookieConsentFromDocument,
  writeCookieConsentToDocument,
} from '@/utils/cookieConsent.utils';

interface ICookieConsentState {
  consent: ICookieConsent | null;
  settingsOpen: boolean;
}

let isCookieConsentSyncInitialized = false;

export const useCookieConsentStore = defineStore('cookieConsent', {
  state: (): ICookieConsentState => ({
    consent: null,
    settingsOpen: false,
  }),

  getters: {
    hasStoredConsent: (state): boolean => state.consent !== null,
    hasAnalyticsConsent: (state): boolean => state.consent?.analytics === true,
    shouldShowBanner(): boolean {
      return !this.hasStoredConsent || this.settingsOpen;
    },
  },

  actions: {
    init(): void {
      this.syncFromCookie();

      if (isCookieConsentSyncInitialized || typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }

      const syncFromCookie = (): void => {
        this.syncFromCookie();
      };

      const syncFromVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') {
          this.syncFromCookie();
        }
      };

      window.addEventListener('focus', syncFromCookie);
      window.addEventListener('pageshow', syncFromCookie);
      window.addEventListener('storage', (event) => {
        if (event.key === COOKIE_CONSENT_CHANGE_KEY || event.key === null) syncFromCookie();
      });
      document.addEventListener('visibilitychange', syncFromVisibilityChange);

      isCookieConsentSyncInitialized = true;
    },

    syncFromCookie(): void {
      this.consent = readCookieConsentFromDocument();
    },

    openSettings(): void {
      this.syncFromCookie();
      this.settingsOpen = true;
    },

    closeSettings(): void {
      this.settingsOpen = false;
    },

    acceptAll(): void {
      this.persistConsent(createCookieConsent({ analytics: true, marketing: true }));
    },

    acceptSelection(selection: ICookieConsentSelection): void {
      this.persistConsent(createCookieConsent(selection));
    },

    persistConsent(consent: ICookieConsent): void {
      writeCookieConsentToDocument(consent);
      this.consent = consent;
      this.settingsOpen = false;
    },
  },
});
